/**
 * Etapas da abertura (E126.F2, GUS-105; E144.F3, GUS-135): Enviar documentos → Conferir →
 * Acompanhar. O mesmo desenho de disco e linha do stepper do portal (E50.F8): feito = disco com
 * check, corrente = anel com halo, futuro = neutro. Sem cor como única pista: o estado vai em
 * `data-state` e o corrente em `aria-current`.
 */
import { Check } from 'lucide-react';

import { ptBR } from '@/i18n/pt-BR';
import { PASSOS_DA_JORNADA, type PassoDaAbertura } from '@/lib/abertura';
import { cn } from '@/lib/utils';

const t = ptBR.abertura.passos;

type EstadoDoPasso = 'feito' | 'corrente' | 'futuro';

// E163.F3 (GUS-209 A67-17): as etapas do HTML 6.7 — 12px, o número numa caixa reta de Barlow
// Condensed; a corrente em ciano, a feita cheia com o check.
const TEXTO: Record<EstadoDoPasso, string> = {
  feito: 'text-fg',
  corrente: 'text-accent-700',
  futuro: 'text-fg',
};

const DISCO: Record<EstadoDoPasso, string> = {
  feito: 'border-accent-700 bg-accent-700 text-fg-onInverse',
  corrente: 'border-accent bg-accent-bg text-accent-700',
  futuro: 'border-border bg-surface-2 text-fg-muted',
};

export interface AberturaStepperProps {
  passoAtual: PassoDaAbertura;
  passos?: readonly PassoDaAbertura[];
}

export function AberturaStepper({
  passoAtual,
  passos = PASSOS_DA_JORNADA,
}: AberturaStepperProps): JSX.Element {
  const atual = passos.indexOf(passoAtual);
  return (
    <ol
      className="flex flex-wrap items-center gap-y-2"
      aria-label={t.ariaLabel}
      data-testid="abertura-stepper"
    >
      {passos.map((passo, idx) => {
        const estado: EstadoDoPasso = idx < atual ? 'feito' : idx === atual ? 'corrente' : 'futuro';
        return (
          <li key={passo} className="flex min-w-0 items-center">
            {idx > 0 ? (
              <span aria-hidden className="mx-[10px] h-px w-5 shrink-0 bg-border sm:w-[30px]" />
            ) : null}
            <span
              data-testid={`abertura-etapa-${passo}`}
              data-state={estado}
              aria-current={estado === 'corrente' ? 'step' : undefined}
              className={cn('flex items-center gap-2 text-[12px]', TEXTO[estado])}
            >
              <span
                aria-hidden
                className={cn(
                  'flex shrink-0 items-center justify-center border px-[6px] py-px font-display text-[12px] font-semibold',
                  DISCO[estado],
                )}
              >
                {estado === 'feito' ? <Check className="h-3 w-3" strokeWidth={3} /> : idx + 1}
              </span>
              <span>{t[passo]}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
